// src/app/components/inventory/services/inventory-mock.service.ts
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
  InvKeyType, KeyBalances, OrgInventory, OrgInventoryMeta, InventoryTxn, KEY_ITEM_LABELS, TransferRequest,
} from '../models/inventory.model';

const STORAGE_KEY = 'fs_mock_inventory_v2'; // v2: meta.unlimitedTeamReports added

/** Buyer-account handles (legacy-style: "TestOwnerR6"), not org emails. */
const BUYER_PREFIXES = ['TestOwner', 'GroupCoord', 'AcctOwner', 'Reseller', 'DirectAcct', 'TeamAdmin'];

@Injectable({ providedIn: 'root' })
export class InventoryMockService {
  /** Persisted, transfer-adjusted inventory by org id (overrides the deterministic base). */
  private store: Record<string, OrgInventory> = {};
  private counter = 1;
  private baseCache: Record<string, OrgInventory> = {};

  constructor() {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try { this.store = JSON.parse(saved); } catch { this.store = {}; }
    }
  }

  /** Full inventory for an org (persisted override if present, else the deterministic base). */
  getInventory(orgId: string, orgName: string): Observable<OrgInventory> {
    return of(this.current(orgId, orgName));
  }

  /** Synchronous balances for the listing (cheap, pure-ish). */
  balances(orgId: string, orgName: string): KeyBalances {
    return this.current(orgId, orgName).balances;
  }

  /** Synchronous reseller meta for the listing. */
  meta(orgId: string, orgName: string): OrgInventoryMeta {
    return this.current(orgId, orgName).meta;
  }

  /** Keys of `keyType` an org can actually give away — the cap on a transfer out of it. */
  available(orgId: string, orgName: string, keyType: InvKeyType): number {
    return this.current(orgId, orgName).balances[keyType];
  }

  /**
   * Move keys between two buckets. A real two-sided transfer: the source is debited and the
   * destination credited, each getting its own ledger row.
   *
   * Unassigned is an ordinary party here — a place to park keys that aren't yet attached to an
   * organization or buying account. It holds a balance like anything else, so it can run out.
   * A source can only give what it holds, so the amount is clamped and the moved total returned.
   */
  transfer(req: TransferRequest): Observable<{ moved: number }> {
    const want = Math.max(0, Math.floor(req.amount));
    if (!want || req.from.id === req.to.id) { return of({ moved: 0 }); }

    const src = clone(this.current(req.from.id, req.from.name));
    const moved = Math.min(want, src.balances[req.keyType]);
    if (!moved) { return of({ moved: 0 }); }

    src.balances[req.keyType] -= moved;
    src.ledger = [this.txn(src.orgId, req.keyType, -moved, req.to.name, `to ${req.to.name}`), ...src.ledger];
    this.store[req.from.id] = src;

    // Read the destination after committing the source: if a later change ever lets the two
    // touch the same record, this order keeps the debit visible to the credit.
    const dst = clone(this.current(req.to.id, req.to.name));
    dst.balances[req.keyType] += moved;
    dst.ledger = [this.txn(dst.orgId, req.keyType, moved, req.from.name, `from ${req.from.name}`), ...dst.ledger];
    this.store[req.to.id] = dst;

    this.persist();
    return of({ moved });
  }

  private txn(orgId: string, keyType: InvKeyType, qty: number, org: string, description: string): InventoryTxn {
    return {
      id: `txn-${this.counter++}`,
      ref: `${100000 + Math.floor(hash(orgId + this.counter) % 900000)}`,
      keyType,
      type: 'Transfer',
      date: '1970-01-01T00:00:00Z',   // deterministic; replaced by API later
      item: KEY_ITEM_LABELS[keyType],
      org,
      description,
      qty,
    };
  }

  /** Restore deterministic state (drops persisted transfers). */
  reset(): void {
    this.store = {};
    this.baseCache = {};
    localStorage.removeItem(STORAGE_KEY);
  }

  // --- internals ---
  private current(orgId: string, orgName: string): OrgInventory {
    return this.store[orgId] ?? this.base(orgId, orgName);
  }

  /** Deterministic inventory derived from a hash of the org id (memoized per org id). */
  private base(orgId: string, orgName: string): OrgInventory {
    if (this.baseCache[orgId]) { return this.baseCache[orgId]; }
    const built = this.buildBase(orgId, orgName);
    this.baseCache[orgId] = built;
    return built;
  }

  private buildBase(orgId: string, orgName: string): OrgInventory {
    const pick = (salt: number, mod: number) => hash(orgId + ':' + salt) % mod;

    const balances: KeyBalances = {
      individual: pick(1, 30),
      group: pick(2, 16),
      team: pick(3, 6),
    };
    const meta: OrgInventoryMeta = {
      results: pick(4, 20),
      pricing: pick(5, 2) === 0 ? 'Business' : 'Standard',
      buyingType: pick(6, 2) === 0 ? 'Reseller' : 'Direct',
      buyer: BUYER_PREFIXES[pick(7, BUYER_PREFIXES.length)] + 'R' + (pick(8, 9) + 1),
      // Roughly a third of orgs already have it, so the upgraded state is reachable without
      // clicking Upgrade — and survives a reseed.
      unlimitedTeamReports: pick(9, 3) === 0,
    };

    // A few deterministic ledger rows per key type so the detail view has history.
    const ledger: InventoryTxn[] = [];
    (['individual', 'group', 'team'] as InvKeyType[]).forEach((kt, ki) => {
      const rows = 1 + (pick(10 + ki, 3));
      for (let r = 0; r < rows; r++) {
        const q = 1 + pick(20 + ki * 5 + r, 9);
        ledger.push({
          id: `${orgId}-${kt}-${r}`,
          ref: `${200000 + (pick(30 + ki * 7 + r, 90000))}`,
          keyType: kt,
          type: r === 0 ? 'Transfer' : 'Profile',
          date: '1970-01-01T00:00:00Z',
          item: KEY_ITEM_LABELS[kt],
          org: 'Unassigned',
          description: r === 0 ? 'from Unassigned' : 'profile scored',
          qty: r === 0 ? q : -1,
        });
      }
    });

    return { orgId, orgName, balances, meta, ledger };
  }

  private persist(): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.store));
  }
}

/** Small stable non-negative string hash (djb2). */
function hash(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) { h = ((h << 5) + h + s.charCodeAt(i)) >>> 0; }
  return h;
}

function clone<T>(v: T): T { return JSON.parse(JSON.stringify(v)); }
